import React from "react";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { useAuth } from "../../app/auth/useAuth";
import {
  TransferOrderResolution,
  transferOrderResolutionOptions,
  type BankOrderPreview,
  type BankOrderResult,
  type BankOrderStateValue
} from "../../app/api/bankOrders";
import { useI18n } from "../../app/i18n/useI18n";
import { BankOrderResolveModal } from "./BankOrderResolveModal";
import { BankOrderSubmitDialog } from "./BankOrderSubmitDialog";

export type RefundTransferTask =
  | { kind: "submit"; transferOrderIds: string[] }
  | { kind: "resolve"; transferOrderId: string; state: BankOrderStateValue };

/**
 * Rückerstattung per Überweisung an die Bank senden oder klären — direkt nach dem Storno und aus den
 * Zahlungen der Akte. Eine Stelle für Vorschau, Senden und Klärung, damit beide Einstiege gleich arbeiten.
 */
export function RefundTransferDialogs({ task, onClose, onDone }: {
  task: RefundTransferTask | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const auth = useAuth();
  const { text } = useI18n();
  const feedback = useFeedback();
  const [preview, setPreview] = React.useState<{ ids: string[]; preview: BankOrderPreview } | null>(null);
  const submitIds = task?.kind === "submit" ? task.transferOrderIds : null;

  React.useEffect(() => {
    if (!submitIds || submitIds.length === 0) return;
    let alive = true;
    auth.api.post<BankOrderPreview>("/api/v1/finance/transfer-orders/submission-preview", { ids: submitIds })
      .then((result) => { if (alive && result) setPreview({ ids: submitIds, preview: result }); else if (alive) onClose(); })
      // Der zentrale API-Handler zeigt den Grund (keine Verbindung, verschiedene Konten).
      .catch(() => { if (alive) onClose(); });
    return () => { alive = false; };
  }, [auth.api, submitIds, onClose]);

  const resolveTarget = task?.kind === "resolve" ? task : null;
  return (
    <>
      <BankOrderSubmitDialog preview={preview?.preview ?? null} title={text("Transfer refunds")} okText={text("Banking send to bank")}
        submit={(body, signal) => auth.api.post<BankOrderResult>("/api/v1/finance/transfer-orders/submit",
          { ...body, ids: preview?.ids ?? [] }, { signal, suppressGlobalErrorToast: true })}
        onClose={() => { setPreview(null); onClose(); }}
        onResult={(result) => {
          if (result.success) feedback.success(text(result.message));
          else feedback.error(text(result.message));
          onDone();
        }} />
      <BankOrderResolveModal open={!!resolveTarget} title={text("Banking resolve transfer")}
        options={transferOrderResolutionOptions(resolveTarget?.state).map((value) => ({
          value,
          label: text(value === TransferOrderResolution.Submitted ? "Banking order arrived at bank"
            : value === TransferOrderResolution.NotSubmitted ? "Banking order not at bank" : "Transfer done outside m1member")
        }))}
        onCancel={onClose}
        onResolve={async (resolution) => {
          if (!resolveTarget) return;
          await auth.api.post("/api/v1/finance/transfer-orders/resolve", { ids: [resolveTarget.transferOrderId], resolution });
          onClose();
          onDone();
        }} />
    </>
  );
}
